/**
 * Phase 261b — Profile/Settings affordance for Out-Play PWA install.
 *
 * Small, role-agnostic card that gives the user three jobs in one place:
 *   1. See whether Out-Play is already installed on this device.
 *   2. Re-trigger the native install prompt on demand (if eligible).
 *   3. Reset the in-app banner's 14-day dismissal so it reappears immediately.
 *
 * Designed to live inside Profile.js / Settings.js without leaking PWA
 * implementation details into those pages.
 */
import { useEffect, useState, useCallback, useRef } from 'react';
import { Smartphone, Monitor, CheckCircle2, Download, Share, Plus, RefreshCw, Sparkles } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { usePwaInstall } from '@/lib/pwaInstall';
import { useAuth } from '@/contexts/AuthContext';
import { clearPwaDismissal, getPwaDismissalState } from '@/components/PwaInstallBanner';
import { trackPwa, personaForRole } from '@/lib/pwaTelemetry';
import { toast } from 'sonner';

function formatDate(d) {
  if (!d) return '';
  try {
    return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
  } catch (_) {
    return d.toString();
  }
}

export default function PwaInstallCard() {
  const { canInstall, installed, isStandalone, isIos, promptInstall } = usePwaInstall();
  const { user } = useAuth();
  const [dismissalState, setDismissalState] = useState({ dismissed: false, expiresAt: null });
  const [busy, setBusy] = useState(false);
  const viewedEventFired = useRef(false);  // Phase 261c — once per mount

  const refreshDismissal = useCallback(() => {
    setDismissalState(getPwaDismissalState());
  }, []);

  useEffect(() => { refreshDismissal(); }, [refreshDismissal]);

  // Phase 261c — funnel: card was rendered for this user (fire once per mount,
  // regardless of which state it's in — useful for "saw install affordance" base).
  useEffect(() => {
    if (viewedEventFired.current) return;
    viewedEventFired.current = true;
    const persona = personaForRole(user?.role);
    const state = (installed || isStandalone) ? 'installed'
      : (isIos && !canInstall) ? 'ios'
      : canInstall ? 'eligible' : 'pending';
    trackPwa('pwa_card_viewed', {
      persona, role: user?.role, source: 'profile_card',
      card_state: state, in_cooldown: dismissalState.dismissed,
    });
  }, [user?.role, installed, isStandalone, isIos, canInstall, dismissalState.dismissed]);

  const isAlreadyInstalled = installed || isStandalone;

  // ────── State 1: Already installed
  if (isAlreadyInstalled) {
    return (
      <Card data-testid="pwa-install-card" data-state="installed" className="border-l-4 border-l-emerald-500">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-emerald-600" /> Out-Play is installed
          </CardTitle>
          <CardDescription>
            You're running Out-Play as a standalone app on this device — no browser tab needed. Launch it from your taskbar / dock / home screen.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  const handleInstallClick = async () => {
    if (!canInstall) return;
    setBusy(true);
    const persona = personaForRole(user?.role);
    trackPwa('pwa_install_prompted', { persona, role: user?.role, source: 'profile_card' });
    try {
      const accepted = await promptInstall();
      trackPwa(accepted ? 'pwa_install_accepted' : 'pwa_install_rejected', {
        persona, role: user?.role, source: 'profile_card',
      });
      if (accepted) {
        toast.success('Out-Play installed — launch it from your taskbar or home screen.');
      } else {
        toast.info('Install cancelled. You can try again anytime.');
      }
      refreshDismissal();
    } finally {
      setBusy(false);
    }
  };

  const handleShowBannerAgain = () => {
    const ok = clearPwaDismissal();
    if (ok) {
      trackPwa('pwa_card_cooldown_cleared', {
        persona: personaForRole(user?.role), role: user?.role, source: 'profile_card',
      });
      toast.success('We\'ll surface the install banner again on your next dashboard visit.');
      refreshDismissal();
    } else {
      toast.error('Could not clear the dismissal — your browser may be in private mode.');
    }
  };

  // ────── State 2: iOS Safari (no native prompt; instruct user)
  if (isIos && !canInstall) {
    return (
      <Card data-testid="pwa-install-card" data-state="ios" className="border-l-4 border-l-sky-500">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Smartphone className="h-5 w-5 text-sky-600" /> Add Out-Play to your home screen
          </CardTitle>
          <CardDescription>
            On iPhone / iPad, Safari can't auto-install — it's a one-tap manual step.
          </CardDescription>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground space-y-2">
          <p className="flex items-center gap-1.5">
            1. Tap the
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-sky-50 border border-sky-200 text-sky-800">
              <Share className="h-3 w-3" /> Share
            </span>
            icon at the bottom of Safari.
          </p>
          <p className="flex items-center gap-1.5">
            2. Scroll and choose
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-sky-50 border border-sky-200 text-sky-800">
              <Plus className="h-3 w-3" /> Add to Home Screen
            </span>.
          </p>
          <p className="pt-1 text-xs italic">
            Out-Play will land beside your favourite apps — no browser tabs, full-screen launch.
          </p>
        </CardContent>
      </Card>
    );
  }

  // ────── State 3 & 4: Chrome / Android / Edge / Brave — installable or dismissed
  const cooldownRemaining = dismissalState.dismissed && dismissalState.expiresAt;

  return (
    <Card data-testid="pwa-install-card" data-state={canInstall ? 'eligible' : 'pending'} className="border-l-4 border-l-emerald-500">
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <Monitor className="h-5 w-5 text-emerald-600" />
          Install Out-Play on this device
        </CardTitle>
        <CardDescription>
          Pin Out-Play to your taskbar / dock / home screen for one-tap launch — no browser tabs, no URL typing.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {canInstall ? (
          <Button
            onClick={handleInstallClick}
            disabled={busy}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="pwa-install-card-install-btn"
          >
            {busy ? <RefreshCw className="h-4 w-4 mr-1.5 animate-spin" /> : <Download className="h-4 w-4 mr-1.5" />}
            {busy ? 'Opening installer…' : 'Install Out-Play now'}
          </Button>
        ) : (
          <div className="flex items-start gap-2 p-3 rounded-lg bg-slate-50 dark:bg-slate-900/40 border border-slate-200 dark:border-slate-800 text-xs text-slate-700 dark:text-slate-300">
            <Sparkles className="h-3.5 w-3.5 mt-0.5 text-slate-500 shrink-0" />
            <div className="space-y-1">
              <p className="font-medium text-slate-900 dark:text-slate-100">
                Your browser isn't showing the install prompt yet.
              </p>
              <p>
                Browser PWA install requires Chrome, Edge, or Brave on desktop / Android, and a few page interactions
                first. Try refreshing this page after using Out-Play for a minute — the install icon (<strong>⊕</strong>)
                appears at the right edge of the address bar.
              </p>
            </div>
          </div>
        )}

        {cooldownRemaining && (
          <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/60">
            <div className="flex-1 text-xs text-amber-900 dark:text-amber-100 space-y-1">
              <p className="font-medium">
                You dismissed the install banner earlier.
              </p>
              <p>
                We'll surface it again automatically on{' '}
                <Badge variant="outline" className="text-[10px] border-amber-300 text-amber-900 dark:text-amber-100" data-testid="pwa-install-card-remind-date">
                  {formatDate(dismissalState.expiresAt)}
                </Badge>
                . Want to see it before then?
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleShowBannerAgain}
              className="shrink-0 text-amber-900 dark:text-amber-100 border-amber-300"
              data-testid="pwa-install-card-clear-dismissal"
            >
              <RefreshCw className="h-3.5 w-3.5 mr-1" /> Show banner again
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
